import { useState, useMemo, useCallback, useRef } from "react";
import BlockEditor, { textToBlocks } from "./Notes/BlockEditor";
import { api } from "../api/client";

const GENERAL = "General";
const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';
const YELLOW = '#E4E69B';

const PRESET_COLOURS = [
  '#b0bec5', '#E4E69B', '#4fc3f7', '#81c784',
  '#ef9a9a', '#ce93d8', '#ffcc80', '#80cbc4',
];

function Notes({
  quickNotes,
  setQuickNotes,
  newNote,
  setNewNote,
  searchNote,
  setSearchNote,
  deleteNote,
  setViewNote,
  viewNote,
  formatNoteDate,
  noteCategories,
  setNoteCategories,
}) {
  const isMobile = typeof window !== "undefined"
    && window.matchMedia('(max-width: 767px)').matches;
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [viewFilter, setViewFilter] = useState("All");
  const [openNote, setOpenNote] = useState(null);
  const [sortBy, setSortBy] = useState("recent");
  const [hoveredId, setHoveredId] = useState(null);

  // ── Sidebar category state ─────────────────────────────────────────────────
  const [hoveredCatId, setHoveredCatId] = useState(null);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [editCatColour, setEditCatColour] = useState('');
  const [confirmDelCatId, setConfirmDelCatId] = useState(null);

  // ── New category inline form ───────────────────────────────────────────────
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColour, setNewCatColour] = useState(PRESET_COLOURS[1]);

  // ── Manage Categories modal ────────────────────────────────────────────────
  const [showManageCats, setShowManageCats] = useState(false);
  const [manageCatEditId, setManageCatEditId] = useState(null);
  const [manageCatName, setManageCatName] = useState('');
  const [manageCatColour, setManageCatColour] = useState('');
  const [manageCatConfDel, setManageCatConfDel] = useState(null);

  // ── Note editor: category selector for new category inline ────────────────
  const [editorCatMode, setEditorCatMode] = useState('select'); // 'select' | 'new'
  const [editorNewCatName, setEditorNewCatName] = useState('');
  const [editorNewCatCol, setEditorNewCatCol] = useState(PRESET_COLOURS[1]);

  const saveTimerRef = useRef(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Category helpers ───────────────────────────────────────────────────────
  const catById = useCallback((id) => noteCategories.find(c => c.id === id) || null, [noteCategories]);

  function noteCatObj(note) {
    if (note?.categoryId) return catById(note.categoryId);
    return null;
  }
  function noteCatName(note) { return noteCatObj(note)?.name || note?.project || GENERAL; }
  function noteCatColour(note) { return noteCatObj(note)?.colour || '#b0bec5'; }

  // ── Category CRUD ──────────────────────────────────────────────────────────
  async function createCategory(name, colour) {
    if (!name.trim()) return null;
    try {
      const res = await api('/api/note-categories', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), colour }),
      });
      const data = await res.json();
      if (data.category) {
        setNoteCategories(prev => [...prev, data.category]);
        return data.category;
      }
    } catch (err) {
      console.error('Failed to create category:', err);
    }
    return null;
  }

  async function updateCategory(id, name, colour) {
    try {
      const res = await api(`/api/note-categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name, colour }),
      });
      const data = await res.json();
      if (data.category) {
        setNoteCategories(prev => prev.map(c => c.id === id ? data.category : c));
      }
    } catch (err) {
      console.error('Failed to update category:', err);
    }
  }

  async function deleteCategory(id) {
    try {
      await api(`/api/note-categories/${id}`, { method: 'DELETE' });
      setNoteCategories(prev => prev.filter(c => c.id !== id));
      // Clear categoryId from local note state for affected notes
      setQuickNotes(prev => prev.map(n => n.categoryId === id ? { ...n, categoryId: null } : n));
      if (viewFilter === id) setViewFilter('All');
    } catch (err) {
      console.error('Failed to delete category:', err);
    }
  }

  // ── Note CRUD ──────────────────────────────────────────────────────────────
  function handleAddNote() {
    const note = {
      id: String(Date.now()),
      title: "",
      emoji: "",
      blocks: textToBlocks(""),
      text: "",
      created: new Date().toISOString(),
      categoryId: viewFilter !== 'All' ? viewFilter : null,
    };
    setQuickNotes([note, ...quickNotes]);
    setOpenNote(note);
    api('/api/notes', { method: 'POST', body: JSON.stringify(note) })
      .catch(err => console.error('Failed to create note:', err));
  }

  const handleEditorChange = useCallback((updatedNote) => {
    setQuickNotes(prev => prev.map(n => n.id === updatedNote.id ? updatedNote : n));
    setOpenNote(updatedNote);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api(`/api/notes/${updatedNote.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: updatedNote.title,
          emoji: updatedNote.emoji,
          blocks: updatedNote.blocks,
          text: updatedNote.text,
          project: updatedNote.project,
          categoryId: updatedNote.categoryId || null,
        }),
      }).catch(err => console.error('Failed to auto-save note:', err));
    }, 1000);
  }, [setQuickNotes]);

  async function handleEditorCategoryChange(catValue) {
    if (catValue === '__new__') {
      setEditorCatMode('new');
      setEditorNewCatName('');
      setEditorNewCatCol(PRESET_COLOURS[1]);
    } else {
      setEditorCatMode('select');
      handleEditorChange({ ...openNote, categoryId: catValue || null });
    }
  }

  async function handleSaveAndBack() {
    if (!openNote) return;
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    setSaving(true);
    try {
      await api(`/api/notes/${openNote.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: openNote.title,
          emoji: openNote.emoji,
          blocks: openNote.blocks,
          text: openNote.text,
          project: openNote.project,
          categoryId: openNote.categoryId || null,
        }),
      });
      setSavedFlash(true);
      setTimeout(() => {
        setSavedFlash(false);
        setSaving(false);
        setOpenNote(null);
        setEditorCatMode('select');
      }, 700);
    } catch (err) {
      console.error('Failed to save note:', err);
      setSaving(false);
    }
  }

  async function confirmEditorNewCat() {
    if (!editorNewCatName.trim()) return;
    const cat = await createCategory(editorNewCatName.trim(), editorNewCatCol);
    if (cat) {
      setEditorCatMode('select');
      handleEditorChange({ ...openNote, categoryId: cat.id });
    }
  }

  // ── Filtering & sorting ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return quickNotes.filter(note => {
      if (!note) return false;
      const q = (searchNote || "").toLowerCase();
      const matchText = !q ||
        (note.text || "").toLowerCase().includes(q) ||
        (note.title || "").toLowerCase().includes(q);
      let matchCat = true;
      if (viewFilter !== 'All') {
        matchCat = (note.categoryId || null) === viewFilter;
      }
      return matchText && matchCat;
    });
  }, [quickNotes, searchNote, viewFilter]);

  const sorted = useMemo(() => {
    const base = [...filtered];
    if (sortBy === 'recent') return base.sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0));
    if (sortBy === 'alpha') return base.sort((a, b) => (a.title || a.text || '').localeCompare(b.title || b.text || ''));
    if (sortBy === 'category') {
      return base.sort((a, b) => {
        const na = noteCatName(a), nb = noteCatName(b);
        return na.localeCompare(nb) || new Date(b.created || 0) - new Date(a.created || 0);
      });
    }
    return base;
  }, [filtered, sortBy, noteCategories]);

  // ── Block editor view ──────────────────────────────────────────────────────
  if (openNote) {
    const currentCatId = openNote.categoryId || '';
    return (
      <div style={{ minHeight: '80vh', fontFamily: 'Raleway, sans-serif' }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.75rem 1.5rem', borderBottom: `1px solid ${BORDER}`,
          background: '#fff', position: 'sticky', top: 0, zIndex: 100,
        }}>
          <button
            onClick={() => { setOpenNote(null); setEditorCatMode('select'); }}
            style={{
              background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
              padding: '0.35rem 0.85rem', cursor: 'pointer', fontSize: '0.88rem',
              color: '#555', fontFamily: 'Raleway, sans-serif',
            }}
          >
            ← Back
          </button>

          {/* Category selector */}
          {editorCatMode === 'select' ? (
            <select
              value={currentCatId}
              onChange={e => handleEditorCategoryChange(e.target.value)}
              style={{
                padding: '0.35rem 0.65rem', borderRadius: 6, border: `1px solid ${BORDER}`,
                fontSize: '0.88rem', fontFamily: 'Raleway, sans-serif', background: '#fff',
              }}
            >
              <option value="">General</option>
              {noteCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="__new__">＋ New Category…</option>
            </select>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <input
                autoFocus
                type="text"
                placeholder="Category name…"
                value={editorNewCatName}
                onChange={e => setEditorNewCatName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmEditorNewCat(); if (e.key === 'Escape') setEditorCatMode('select'); }}
                style={{
                  padding: '0.3rem 0.55rem', borderRadius: 6, border: `1px solid ${BORDER}`,
                  fontSize: '0.85rem', fontFamily: 'Raleway, sans-serif', width: 130,
                }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                {PRESET_COLOURS.map(col => (
                  <button
                    key={col}
                    onClick={() => setEditorNewCatCol(col)}
                    style={{
                      width: 18, height: 18, borderRadius: '50%', background: col, border: 'none',
                      cursor: 'pointer', outline: editorNewCatCol === col ? `2px solid ${NAVY}` : 'none',
                      outlineOffset: 1,
                    }}
                  />
                ))}
              </div>
              <button
                onClick={confirmEditorNewCat}
                style={{
                  padding: '0.3rem 0.7rem', background: NAVY, color: '#fff', border: 'none',
                  borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                }}
              >
                Add
              </button>
              <button
                onClick={() => setEditorCatMode('select')}
                style={{
                  padding: '0.3rem 0.55rem', background: '#f1f1f1', border: 'none',
                  borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
          )}

          <span style={{ fontSize: '0.78rem', color: '#bbb', marginLeft: 'auto' }}>
            {openNote.updated
              ? `Saved ${formatNoteDate(openNote.updated)}`
              : openNote.created ? `Created ${formatNoteDate(openNote.created)}` : ''}
          </span>

          <button
            onClick={handleSaveAndBack}
            disabled={saving}
            style={{
              background: savedFlash ? '#27ae60' : NAVY,
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '0.35rem 0.9rem', cursor: saving ? 'default' : 'pointer',
              fontSize: '0.88rem', fontWeight: 700, fontFamily: 'Raleway, sans-serif',
              transition: 'background 0.2s', minWidth: 72,
            }}
          >
            {savedFlash ? 'Saved ✓' : saving ? 'Saving…' : 'Save'}
          </button>

          <button
            onClick={() => { deleteNote(openNote.id); setOpenNote(null); }}
            style={{
              background: 'none', border: '1px solid #fcc', borderRadius: 6,
              padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.82rem',
              color: '#E96A6A', fontFamily: 'Raleway, sans-serif',
            }}
          >
            Delete
          </button>
        </div>

        <BlockEditor note={openNote} onChange={handleEditorChange} />
      </div>
    );
  }

  // ── Two-panel list view ────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      minHeight: isMobile ? 'auto' : 'calc(100vh - 4rem)',
      margin: isMobile ? '0' : '-2rem',
      fontFamily: 'Raleway, sans-serif',
      background: BG,
      overflow: isMobile ? 'visible' : 'hidden',
      position: 'relative',
    }}>

      {/* ── Manage Categories Modal ───────────────────────────────────────── */}
      {showManageCats && (
        <div
          onClick={() => { setShowManageCats(false); setManageCatEditId(null); setManageCatConfDel(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
            zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 14, padding: '1.5rem',
              width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              fontFamily: 'Raleway, sans-serif',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '1rem', color: NAVY }}>
                Manage Categories
              </h3>
              <button onClick={() => setShowManageCats(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#aaa' }}>✕</button>
            </div>

            {noteCategories.length === 0 && (
              <p style={{ color: '#aaa', fontSize: '0.88rem', textAlign: 'center', padding: '1rem 0' }}>
                No categories yet.
              </p>
            )}

            {noteCategories.map(cat => (
              <div key={cat.id} style={{ marginBottom: '0.5rem' }}>
                {manageCatEditId === cat.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.6rem', background: '#f8f9fb', borderRadius: 8 }}>
                    <input
                      autoFocus
                      value={manageCatName}
                      onChange={e => setManageCatName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { updateCategory(cat.id, manageCatName, manageCatColour); setManageCatEditId(null); }
                        if (e.key === 'Escape') setManageCatEditId(null);
                      }}
                      style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', fontFamily: 'Raleway, sans-serif' }}
                    />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {PRESET_COLOURS.map(col => (
                        <button
                          key={col}
                          onClick={() => setManageCatColour(col)}
                          style={{
                            width: 20, height: 20, borderRadius: '50%', background: col, border: 'none',
                            cursor: 'pointer', outline: manageCatColour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1,
                          }}
                        />
                      ))}
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => { updateCategory(cat.id, manageCatName, manageCatColour); setManageCatEditId(null); }}
                        style={{ padding: '0.3rem 0.75rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}
                      >Save</button>
                      <button
                        onClick={() => setManageCatEditId(null)}
                        style={{ padding: '0.3rem 0.65rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer' }}
                      >Cancel</button>
                    </div>
                  </div>
                ) : manageCatConfDel === cat.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', background: '#fff3f3', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.83rem', color: '#555', flex: 1 }}>Delete "{cat.name}"?</span>
                    <button
                      onClick={() => { deleteCategory(cat.id); setManageCatConfDel(null); }}
                      style={{ padding: '0.28rem 0.75rem', background: '#E96A6A', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}
                    >Delete</button>
                    <button
                      onClick={() => setManageCatConfDel(null)}
                      style={{ padding: '0.28rem 0.65rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer' }}
                    >Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', borderRadius: 8, background: '#fafaf8' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.colour, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: '0.9rem', color: NAVY }}>{cat.name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#bbb' }}>{quickNotes.filter(n => n.categoryId === cat.id).length} notes</span>
                    <button
                      onClick={() => { setManageCatEditId(cat.id); setManageCatName(cat.name); setManageCatColour(cat.colour); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '0.82rem', padding: '2px 6px' }}
                      title="Edit"
                    >✏️</button>
                    <button
                      onClick={() => setManageCatConfDel(cat.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E96A6A', fontSize: '0.82rem', padding: '2px 6px' }}
                      title="Delete"
                    >🗑️</button>
                  </div>
                )}
              </div>
            ))}

            {/* Add new inside modal */}
            <div style={{ marginTop: '1rem', borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
              <AddCategoryInline
                onAdd={async (name, colour) => {
                  await createCategory(name, colour);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile sidebar overlay ───────────────────────────────────────── */}
      <div
        className={`notes-sidebar-overlay${sidebarOpen ? ' overlay-visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ───────────────────────────────────────────────────────── */}
      <div
        className={`notes-sidebar-drawer${sidebarOpen ? ' sidebar-open' : ''}`}
        style={{
          width: isMobile ? '82vw' : 240,
          maxWidth: isMobile ? 320 : 'none',
          background: '#fff', borderRight: `1px solid ${BORDER}`,
          display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 14px 12px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 800, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Notes
            </span>
            <button
              onClick={handleAddNote}
              style={{ background: YELLOW, border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: NAVY }}
            >
              + New
            </button>
          </div>
        </div>

        {/* Category list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {/* All Notes */}
          <div
            onClick={() => { setViewFilter('All'); if (window.innerWidth < 768) setSidebarOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
              background: viewFilter === 'All' ? `${NAVY}0d` : 'transparent',
              marginBottom: 2,
            }}
          >
            <span style={{ fontSize: 14 }}>📝</span>
            <span style={{ fontSize: 13, fontWeight: viewFilter === 'All' ? 700 : 400, color: NAVY, flex: 1 }}>All Notes</span>
            <span style={{ fontSize: 11, color: '#aaa' }}>{quickNotes.length}</span>
          </div>

          <div style={{ height: 1, background: BORDER, margin: '6px 4px' }} />

          {noteCategories.map(cat => {
            const count = quickNotes.filter(n => n.categoryId === cat.id).length;
            const isActive = viewFilter === cat.id;
            const isHov = hoveredCatId === cat.id;
            const isEditing = editingCatId === cat.id;
            const isConfDel = confirmDelCatId === cat.id;

            return (
              <div
                key={cat.id}
                onMouseEnter={() => setHoveredCatId(cat.id)}
                onMouseLeave={() => setHoveredCatId(null)}
                style={{ borderRadius: 8, background: isActive ? `${NAVY}0d` : 'transparent', marginBottom: 1 }}
              >
                {isEditing ? (
                  <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <input
                      autoFocus
                      value={editCatName}
                      onChange={e => setEditCatName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { updateCategory(cat.id, editCatName, editCatColour); setEditingCatId(null); }
                        if (e.key === 'Escape') setEditingCatId(null);
                      }}
                      style={{ padding: '0.28rem 0.5rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.82rem', fontFamily: 'Raleway, sans-serif', width: '100%', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {PRESET_COLOURS.map(col => (
                        <button
                          key={col}
                          onClick={() => setEditCatColour(col)}
                          style={{ width: 16, height: 16, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: editCatColour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }}
                        />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button
                        onClick={() => { updateCategory(cat.id, editCatName, editCatColour); setEditingCatId(null); }}
                        style={{ flex: 1, padding: '0.28rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                      >Save</button>
                      <button
                        onClick={() => setEditingCatId(null)}
                        style={{ padding: '0.28rem 0.5rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer' }}
                      >✕</button>
                    </div>
                  </div>
                ) : isConfDel ? (
                  <div style={{ padding: '6px 8px' }}>
                    <p style={{ margin: '0 0 0.35rem', fontSize: '0.78rem', color: '#555' }}>Delete "{cat.name}"?</p>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button
                        onClick={() => { deleteCategory(cat.id); setConfirmDelCatId(null); }}
                        style={{ flex: 1, padding: '0.28rem', background: '#E96A6A', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                      >Delete</button>
                      <button
                        onClick={() => setConfirmDelCatId(null)}
                        style={{ padding: '0.28rem 0.5rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer' }}
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      onClick={() => { setViewFilter(cat.id); if (window.innerWidth < 768) setSidebarOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, padding: '7px 8px', cursor: 'pointer', minWidth: 0 }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.colour, flexShrink: 0, border: '1px solid rgba(0,0,0,0.08)' }} />
                      <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: NAVY, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</span>
                      <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0 }}>{count}</span>
                    </div>
                    {isHov && (
                      <div style={{ display: 'flex', gap: 2, paddingRight: 4 }}>
                        <button
                          onClick={e => { e.stopPropagation(); setEditingCatId(cat.id); setEditCatName(cat.name); setEditCatColour(cat.colour); setConfirmDelCatId(null); }}
                          title="Edit"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '2px 4px', color: '#888', borderRadius: 4 }}
                        >✏️</button>
                        <button
                          onClick={e => { e.stopPropagation(); setConfirmDelCatId(cat.id); setEditingCatId(null); }}
                          title="Delete"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '2px 4px', color: '#E96A6A', borderRadius: 4 }}
                        >🗑️</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* New Category + Manage button */}
        <div style={{ padding: '10px 12px', borderTop: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {showNewCat ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <input
                type="text"
                placeholder="Category name…"
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    if (newCatName.trim()) { await createCategory(newCatName.trim(), newCatColour); }
                    setShowNewCat(false); setNewCatName('');
                  }
                  if (e.key === 'Escape') { setShowNewCat(false); setNewCatName(''); }
                }}
                autoFocus
                style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.85rem', fontFamily: 'Raleway, sans-serif', width: '100%', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {PRESET_COLOURS.map(col => (
                  <button
                    key={col}
                    onClick={() => setNewCatColour(col)}
                    style={{ width: 18, height: 18, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: newCatColour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  onClick={async () => { if (newCatName.trim()) { await createCategory(newCatName.trim(), newCatColour); } setShowNewCat(false); setNewCatName(''); }}
                  style={{ flex: 1, padding: '0.35rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}
                >Add</button>
                <button
                  onClick={() => { setShowNewCat(false); setNewCatName(''); }}
                  style={{ padding: '0.35rem 0.6rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer' }}
                >✕</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowNewCat(true)}
              style={{ width: '100%', padding: '0.4rem', background: 'none', border: `1px dashed ${BORDER}`, borderRadius: 8, fontSize: '0.82rem', color: '#aaa', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}
            >
              + New Category
            </button>
          )}
          <button
            onClick={() => setShowManageCats(true)}
            style={{ width: '100%', padding: '0.38rem', background: 'none', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.8rem', color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}
          >
            ⚙ Manage Categories
          </button>
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="notes-main-content" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <div style={{
          padding: isMobile ? '14px 12px 12px' : '18px 24px 14px', background: '#fff', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          {/* Mobile-only sidebar toggle */}
          <button
            className="notes-sidebar-toggle"
            onClick={() => setSidebarOpen(v => !v)}
            aria-label="Toggle categories"
          >
            Categories
          </button>
          <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '1.1rem', color: NAVY }}>
            {viewFilter === 'All'
              ? 'All Notes'
              : (noteCategories.find(c => c.id === viewFilter)?.name || viewFilter)}
          </h2>
          <span style={{ fontSize: '0.78rem', color: '#aaa', marginRight: 'auto' }}>
            {sorted.length} note{sorted.length !== 1 ? 's' : ''}
          </span>
          <input
            type="text"
            placeholder="Search notes…"
            value={searchNote}
            onChange={e => setSearchNote(e.target.value)}
            style={{
              padding: '0.45rem 0.8rem',
              borderRadius: 8,
              border: `1px solid ${BORDER}`,
              fontSize: '0.88rem',
              fontFamily: 'Raleway, sans-serif',
              minWidth: isMobile ? 0 : 180,
              width: isMobile ? '100%' : 'auto',
              flex: isMobile ? '1 1 100%' : '0 1 auto',
              boxSizing: 'border-box',
            }}
          />
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ padding: '0.45rem 0.75rem', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: '0.88rem', fontFamily: 'Raleway, sans-serif', background: '#fff', cursor: 'pointer' }}
          >
            <option value="recent">Recent</option>
            <option value="alpha">Alphabetical</option>
            <option value="category">Category</option>
          </select>
        </div>

        {/* Card grid */}
        <div style={{ padding: isMobile ? '12px' : '20px 24px', flex: 1 }}>
          {sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 0', color: '#bbb' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📝</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#aaa' }}>No notes here</div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.35rem', color: '#ccc' }}>
                Click{' '}
                <span onClick={handleAddNote} style={{ color: NAVY, cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>
                  + New
                </span>
                {' '}to create your first note.
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(230px, 1fr))', gap: isMobile ? '0.75rem' : '1rem' }}>
              {sorted.map(note => {
                const isHovered = hoveredId === note.id;
                const hasTitle = !!note.title?.trim();
                const title = hasTitle ? note.title.trim() : (note.text?.trim().split(/\s+/).slice(0, 7).join(' ') || 'Untitled');
                const preview = hasTitle ? (note.text || '').slice(0, 130) : (note.text?.trim().split(/\s+/).slice(7).join(' ') || '').slice(0, 100);
                const catName = noteCatName(note);
                const catColour = noteCatColour(note);

                return (
                  <div
                    key={note.id}
                    onMouseEnter={() => setHoveredId(note.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => setOpenNote(note)}
                    style={{
                      background: '#fff', border: `1px solid ${isHovered ? `${NAVY}30` : BORDER}`,
                      borderRadius: 12, padding: '1rem 1.1rem', cursor: 'pointer',
                      position: 'relative', transition: 'box-shadow 0.15s, border-color 0.15s',
                      boxShadow: isHovered ? '0 4px 16px rgba(12,45,72,0.1)' : '0 1px 4px rgba(0,0,0,0.05)',
                      display: 'flex', flexDirection: 'column', gap: '0.45rem', minHeight: 140,
                    }}
                  >
                    {isHovered && (
                      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
                        <button onClick={e => { e.stopPropagation(); setOpenNote(note); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: '0.72rem', color: NAVY, fontWeight: 700, fontFamily: 'Raleway, sans-serif' }}>Edit</button>
                        <button onClick={e => { e.stopPropagation(); deleteNote(note.id); }} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: '0.72rem', color: '#E96A6A', fontWeight: 700, fontFamily: 'Raleway, sans-serif' }}>Delete</button>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.35rem', paddingRight: isHovered ? 90 : 0 }}>
                      {note.emoji && <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>{note.emoji}</span>}
                      <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: NAVY, lineHeight: 1.35 }}>{title}</span>
                    </div>
                    {preview && (
                      <p style={{ margin: 0, fontSize: '0.81rem', color: '#666', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {preview}
                      </p>
                    )}
                    <div style={{ flex: 1 }} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: catColour + '28', border: `1px solid ${catColour}66`, borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700, color: '#555', maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: catColour, display: 'inline-block', flexShrink: 0 }} />
                        {catName}
                      </span>
                      {note.created && <span style={{ fontSize: '0.7rem', color: '#bbb', flexShrink: 0 }}>{formatNoteDate(note.created)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Reusable inline add-category form used in the Manage Categories modal
function AddCategoryInline({ onAdd }) {
  const [name, setName] = useState('');
  const [colour, setColour] = useState(PRESET_COLOURS[1]);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    await onAdd(name.trim(), colour);
    setName(''); setColour(PRESET_COLOURS[1]); setBusy(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <input
        type="text"
        placeholder="New category name…"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        style={{ padding: '0.45rem 0.65rem', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: '0.9rem', fontFamily: 'Raleway, sans-serif' }}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        {PRESET_COLOURS.map(col => (
          <button
            key={col}
            onClick={() => setColour(col)}
            style={{ width: 22, height: 22, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: colour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }}
          />
        ))}
      </div>
      <button
        onClick={submit}
        disabled={!name.trim() || busy}
        style={{ padding: '0.45rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.88rem', fontWeight: 700, cursor: name.trim() ? 'pointer' : 'not-allowed', opacity: name.trim() ? 1 : 0.5 }}
      >
        + Add Category
      </button>
    </div>
  );
}

export default Notes;
